import TrackingPageApi from '../api/TrackingPageApiLayer';
import LayoutTypes from '../../constants/layoutTypes';
import RowHeightTypes from '../../constants/rowHeightTypes';
import ComponentTypes from '../../constants/componentTypes';
import RowTextPositions from '../../constants/rowTextPositions';
import reactKeyGenerator from '../../helpers/reactKeyGenerator';

const getPreparedShipmentEvent = (shipmentEvent) => ({
  renderKey: reactKeyGenerator.key(),
  timeStamp: shipmentEvent.timestamp,
  state: shipmentEvent.state,
});

const getPreparedShipmentEvents = (shipmentEvents) => shipmentEvents
  .map((shipmentEvent) => getPreparedShipmentEvent(shipmentEvent));

const getPreparedShipment = (shipment) => ({
  id: shipment.id,
  carrier: shipment.carrier,
  carrierId: shipment.carrier_id,
  shipmentState: {
    stateLabel: shipment.shipment_state.state_label,
    state: shipment.shipment_state.state,
  },
  signee: shipment.signee,
  trackingReference: shipment.tracking_reference,
  expectedDeliveryDate: shipment.expected_delivery_date,
  deliveredDate: shipment.delivered_date,
  deliverySlotInterval: shipment.delivery_slot_interval,
  events: shipment.events
    ? getPreparedShipmentEvents(shipment.events)
    : null,
  carrierTrackingPage: shipment.carrier_tracking_page,
  metadataName: shipment.metadata_name,
  logoUrl: shipment.logo_url,
});

const getPreparedFont = (font, globalFont) => ({
  name: font.name || globalFont.name,
  url: font.url || globalFont.url,
  size: font.size || globalFont.size,
  fontWeight: font.font_weight || globalFont.font_weight,
  colour: font.colour || globalFont.colour,
});

const getPreparedRowItem = (rowItem, globalFont) => ({
  id: rowItem.id,
  order: rowItem.order,
  base64Image: rowItem.base64_image,
  text: rowItem.text,
  rowTextPosition: RowTextPositions.find((rowTextPosition) => rowTextPosition === rowItem.row_text_position),
  font: rowItem.font
    ? getPreparedFont(rowItem.font, globalFont)
    : undefined,
  redirectionUrl: rowItem.redirection_url,
  componentType: ComponentTypes.find((componentType) => componentType === rowItem.component_type),
});

const getPreparedRow = (row, globalFont) => ({
  id: row.id,
  order: row.order,
  rowHeight: RowHeightTypes.find((rowHeightType) => rowHeightType === row.row_height),
  rowItems: row.row_items?.map((rowItem) => getPreparedRowItem(rowItem, globalFont)) ?? [],
});

const getPreparedNavigationBarItem = (navigationBarItem) => ({
  id: navigationBarItem.id,
  order: navigationBarItem.order,
  text: navigationBarItem.text,
  redirectionUrl: navigationBarItem.redirection_url,
});

const getPreparedNavigationBar = (navigationBar) => ({
  base64Logo: navigationBar.base64_logo,
  backgroundColour: navigationBar.background_colour,
  borderColour: navigationBar.border_colour,
  font: navigationBar.font
    ? getPreparedFont(navigationBar.font)
    : undefined,
  rootUrl: navigationBar.root_url,
  navigationBarItems: navigationBar.navigation_bar_items
    ?.map((navigationBarItem) => getPreparedNavigationBarItem(navigationBarItem))
    ?? [],
});

const getPreparedLayout = (layout, globalFont) => ({
  id: layout.id,
  layoutType: LayoutTypes.find((layoutType) => layoutType === layout.layout_type),
  rows: layout.rows?.map((row) => getPreparedRow(row, globalFont)) ?? [],
});

const getPreparedTrackingPageData = (trackingPageData) => ({
  base64FavIcon: trackingPageData.base64_fav_icon,
  title: trackingPageData.title,
  isCarrierLinkActive: trackingPageData.is_carrier_link_active,
  font: trackingPageData.font
    ? getPreparedFont(trackingPageData.font)
    : undefined,
  navigationBar: trackingPageData.navigation_bar
    ? getPreparedNavigationBar(trackingPageData.navigation_bar)
    : undefined,
  layouts: trackingPageData?.layouts.map((layout) => getPreparedLayout(layout, trackingPageData.font)) ?? [],
});

export const getPreparedTrackingPage = (trackingPage) => {
  const preparedTrackingPage = {
    trackingPageName: trackingPage.tracking_page_name,
    createdOn: trackingPage.created_on,
    publishedOn: trackingPage.published_on,
    version: trackingPage.version,
    showPoweredByFooter: trackingPage.show_powered_by_footer || false,
  };

  return {
    ...preparedTrackingPage,
    trackingPageData: getPreparedTrackingPageData(trackingPage.tracking_page_data),
  };
};

const responseToModel = ({ data, result }) => ({
  result,
  data: data ? {
    trackingPage: getPreparedTrackingPage(data.tracking_page),
    shipments: data.shipments?.map((x) => getPreparedShipment(x)) ?? [],
  } : {
    trackingPage: null,
    shipments: [],
  },
});

class TrackingPageProvider {
  constructor(params) {
    this.api = params.api;
  }

  async getTrackingPage(
    trackingPageNumber,
    shipmentId,
    customerId,
    version,
    trackingReference,
    customReference,
    metadataKey,
    metadataValue,
  ) {
    const response = await this.api.getTrackingPage(
      trackingPageNumber,
      shipmentId,
      customerId,
      version,
      trackingReference,
      customReference,
      metadataKey,
      metadataValue,
    );
    return responseToModel(response);
  }
}

export default new TrackingPageProvider({
  api: TrackingPageApi,
});
